import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of} from 'rxjs';
import {mergeMap, take, map, switchMap} from 'rxjs/operators';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { InterpolateService, InterpolationOptions } from '@ispm/shared/services/interpolate.service';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { isEmpty,set } from 'lodash';
import { CORE_ACTIONS } from '@ispm/core/constants/core-actions.constant';
import {ICustomPostCall, IdataBinding} from "@ispm-client/modules/aggregator/interfaces";
import {HttpInterceptorService} from "@ispm/core/services/http-interceptor.service";
import {SessionStorageService} from "@ispm/shared/services/session-storage.service";
import {StoreActionCreatorService} from "@ispm/shared/services/store-action-creator.service";
import {REPLACE_ACTION_TYPES} from "@ispm-client/modules/aggregator/services/consts";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";
import {UserService} from "@ispm/user/services/user.service";

@Injectable()
export class BundleCall {
  private static createEndPoint = 'bundleUtil/create';
  private static searchEndPoint = 'bundleUtil/search';
  private static upDateEndPoint = 'bundleUtil/update';

  private static customEndPoint = 'multiCall'; //This endpoint should take array of  ICustomPostCall and call those endpoints which is mentioned in payload.

  private interpolateObject: object;

  constructor(private store: Store<IgenomicsState>,
              private _interpolateService: InterpolateService,
              private _ispmHttpClient: IspmHttpClient,
              private _isgHttpClient: HttpInterceptorService,
              private session: SessionStorageService,
              private storeActionService: StoreActionCreatorService,
              private routerManagerService: RouterManagerService,
              private _userService: UserService,
  ) {
    this.interpolateObject = {
      currentPatient: this.setPatientContextForInterpolation(),
      currentTest: {
        revisionId: this.routerManagerService.parameters.revisionId,
        workflowId: this.routerManagerService.parameters.testId
      },
      store: {
        dataBind: this.store.select('forms', 'dataBindings').filter(
          dataBindings => !isEmpty(dataBindings)
        ).take(1).subscribe(data => data),
      }
    };
  }

  public getPatientContext() {
      this.interpolateObject['currentPatient'] = this.routerManagerService.parameters.patientId;
    return this.interpolateObject['currentPatient'];
  }

  public getPatientContextForInterpolation(){

    return {
      currentPatient: this.setPatientContextForInterpolation()
    }
  }

  setInterpolationObject() {
      this.interpolateObject = {
        currentPatient: this.setPatientContextForInterpolation(),
        urlParams: {...this.routerManagerService.parameters},
        currentTest: {
          revisionId: this.routerManagerService.parameters.revisionId,
          workflowId: this.routerManagerService.parameters.testId
        }
      };

  }

  public setPatientContextForInterpolation(){
    let patient_link_list: string[] = []

    this.store.select('users','currentPatientLink')
      .take(1)
      .subscribe((data:any) => {
        patient_link_list = data;
    });

    let patient_link = this.routerManagerService.parameters.patientId;

    if(patient_link_list && patient_link_list.length != 0){
      if(patient_link_list[0] !== "0"){
        patient_link = this.routerManagerService.parameters.patientId + "," + patient_link_list.join();
      }
    }

    return {
      id: this.routerManagerService.parameters.patientId,
      link: patient_link
    }
  }

  private getPatientInfo(){
    let patientEntitytree = `entity-tree/patient/${this.routerManagerService.parameters.patientId}`;
    return this._ispmHttpClient.get(patientEntitytree);
  }

  public async queryFiller(queryString: string): Promise<string> {
    this.setInterpolationObject()
    return await this.getStateObservable().pipe(
       map(state => {
         return Object.assign({ }, this.interpolateObject, state);
       }),
       map((interpolateObject: any) => this._interpolateService.interpolate(queryString, interpolateObject))
     ).toPromise() as Promise<string>;
     }
  public async queryFillerForStore(queryString: string,key): Promise<string> {
    this.interpolateObject = {
      currentPatient: this.setPatientContextForInterpolation(),
      urlParams: {...this.routerManagerService.parameters},
      currentTest: {
        revisionId: this.routerManagerService.parameters.revisionId,
        workflowId: this.routerManagerService.parameters.testId
      }
    };
     let diagnosticOrder=await this._isgHttpClient.get(`diagnosticOrder/${this.session.getdo()}`).toPromise()
    let obj={}
    set (obj,key,diagnosticOrder["body"])
    let finalInterpolateObject=Object.assign({ }, this.interpolateObject,obj );
    return this._interpolateService.interpolate(queryString,finalInterpolateObject )
     }


  public  interPolateQueries(queryStrings: Array<string>): Observable<Array<string>> {
    this.setInterpolationObject()
    return this.getStateObservable().pipe(
      map(state => {
        return Object.assign({ }, this.interpolateObject, state);
      }),
      map((interpolateObject: any) => {
        const interPolatedQueries = [];
        queryStrings.forEach((queryString) => {
          interPolatedQueries.push(this._interpolateService.interpolate(queryString, interpolateObject));
        });
        return interPolatedQueries;
      })
    );
  }


  public getStateObservable(): Observable<IgenomicsState> {
    return this.store.pipe(take(1));
  }

  public getStateProp(prop, element?: IdataBinding): Observable<any>  {
    const n = prop.split('.');
    const propName = n[n.length - 1];
    const propObj = { };
    //TODO wrap everything rather than putting it inkey propName
    return this.storeActionService.getObsValue<any>(prop.split('.'), this.getStateObservable())
      .map(obj => {
        let idx: string = propName;

        if (element && element.replaceAction && element.replaceAction.actionType == REPLACE_ACTION_TYPES.SET_TO_IDENTIFIER) {
          idx = element.identifier;
        }
        propObj[idx] = obj;
        return propObj
      });
  }

  public getState(...props) {
    let state: any = this.store;
    for (const prop of props) {
      state = state.select(prop);
    }
    return state.pipe(mergeMap(value => this.handleStateValue(props, state)));
  }

  public handleStateValue(props: Array<string>, state: any): Promise<any> | Observable<any> {
    return state instanceof Observable || state instanceof Promise
      ? state
      : Promise.resolve(state);
  }

  public searchCall(payload: any) {
    return this._ispmHttpClient.post(BundleCall.searchEndPoint, payload);
  }

  public createCall(payload: any) {

    if(payload['queries'].length > 0) {
         if(this.isPatientEditedByAdmin(payload) && payload['queries'].length === 1){
            return this._userService.createPatient(payload['queries'][0].data).pipe(
              switchMap(res =>  of(res)));
          } else {
            return this._ispmHttpClient.post(BundleCall.createEndPoint, payload);
          }
    } else {
      return of('No data provided');
     }
  }

  public updateCall(payload: any) {

      if(payload['queries'].length > 0) {
          if(this.isPatientEditedByAdmin(payload) && payload['queries'].length === 1){
              return this._userService.updatePatient(payload['queries'][0].data).pipe(
                  switchMap(res =>  of(res)));
          } else {
              return this._ispmHttpClient.put(BundleCall.upDateEndPoint, payload);
          }
      } else {
          return of('No data provided');
      }
  }
  public isPatientEditedByAdmin(payload: any): boolean {
    return payload['queries'][0].data.resourceType === 'Patient' && payload['queries'][0].formId === 'ADMIN-PATIENT-EDITING-DEFAULT';
  }

  public customEndPointCall(postCalls: Array<ICustomPostCall>):Observable<any> {
    return this._ispmHttpClient.post(BundleCall.customEndPoint,postCalls);

  }

  private getUrlParameter(sParam) {
    return decodeURIComponent(window.location.search.substring(1)).split('&')
      .map((v) => {
        v.split('=');
      })
      .filter((v) => {
        return (v[0] === sParam) ? true : false;
      })
      .reduce((prev, curv, index, array) => {
        return curv[1];
      }, undefined);
  }

  public interpolate(query: string, dataObj: any, global = false,
    additionalOptions: InterpolationOptions = InterpolateService.INTERPOLATION_DEFAULT_OPTIONS): string {
    return this._interpolateService.interpolate(query, dataObj, global, additionalOptions);
  }

  public updateLoader(setOrUnset: boolean) {
    this.store.dispatch({
      type: CORE_ACTIONS.SET_LOADER,
      payload: setOrUnset
    });
  }

}
